import { useMemo, type Dispatch, type SetStateAction } from "react";
import { Stack, useTheme } from "@mui/material";
import { BottomSheetDialog, Button } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { FacetFilterPanel, type FacetSelection } from "src/components/FacetFilters";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import {
    adaptFacetSelectionChange,
    toFacetDimensions,
    useAuditLogFacetLabels,
    type AuditLogFacetSection,
} from "./useAuditLogFacetSections";

type AuditLogFilterSheetProps = {
    isOpen: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    sections: AuditLogFacetSection[];
    onSectionChange: (section: AuditLogFacetSection, selection: FacetSelection) => void;
    activeCount: number;
    onReset: () => void;
};

export const AuditLogFilterSheet = (props: AuditLogFilterSheetProps) => {
    const { isOpen, onOpenChange, sections, onSectionChange, activeCount, onReset } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogFacetLabels();
    const { isMobile } = useResponsiveBreakpoints();
    const dimensions = useMemo(() => toFacetDimensions(sections), [sections]);
    const handleSelectionChange = useMemo(
        () => adaptFacetSelectionChange(sections, onSectionChange),
        [sections, onSectionChange],
    );

    if (!isMobile) {
        return null;
    }

    const content = (
        <Stack gap={theme.spacing(3)} paddingX={theme.spacing(2)} paddingBottom={theme.spacing(1)}>
            <FacetFilterPanel dimensions={dimensions} onSelectionChange={handleSelectionChange} labels={labels} />
            <Stack direction="row" gap={theme.spacing(1)}>
                <Button variant="secondary" fullWidth onClick={onReset} disabled={activeCount === 0}>
                    {t("settings.audit_log.filters.reset", "Reset")}
                </Button>
                <Button variant="primary" fullWidth onClick={() => onOpenChange(false)}>
                    {t("settings.audit_log.filters.close", "Close")}
                </Button>
            </Stack>
        </Stack>
    );

    return (
        <BottomSheetDialog
            open={isOpen}
            setOpen={onOpenChange}
            title={t("settings.audit_log.filters", "Filters")}
            content={content as unknown as string}
        />
    );
};
